import { Link } from "@tanstack/react-router";
import { Award } from "lucide-react";
import { useSetBreadcrumbs } from "@/components/shared/breadcrumbs";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { PageHeader } from "@/components/shared/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { useNotStartedCerts } from "@/hooks/use-dashboard-insights";
import { formatDateOnly } from "@/lib/dates";
import { pageOriginSearch } from "@/lib/detail-origin";

/**
 * Admin drill-down behind the Org Health "Certs not started" tile: every
 * certification assigned but not yet started, org-wide, each row linking to the
 * person. Oldest assignment first.
 */
export function NotStartedCertsPage() {
  useSetBreadcrumbs([
    { label: "Administration" },
    { label: "Org Health", to: "/dashboard/org-health" },
    { label: "Certs Not Started" },
  ]);
  const { data, isLoading, isError, error, refetch } = useNotStartedCerts();
  const items = data?.items ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Org Health"
        title="Certifications Not Started"
        description="Certifications that have been assigned but not yet started."
      />

      {isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      ) : isError ? (
        <ErrorState
          message={error?.message ?? "Failed to load certifications"}
          onRetry={() => refetch()}
        />
      ) : items.length === 0 ? (
        <EmptyState
          icon={Award}
          title="Nothing outstanding"
          description="Every assigned certification has been started."
        />
      ) : (
        <div className="overflow-hidden rounded-lg border bg-card">
          {data?.capped && (
            <p className="border-b bg-muted/40 px-4 py-2 text-xs text-muted-foreground">
              Showing the first 200 outstanding assignments.
            </p>
          )}
          <ul className="divide-y">
            {items.map((it) => (
              <li key={it.id}>
                <Link
                  to="/dashboard/persons/$personId"
                  params={{ personId: it.personId }}
                  search={pageOriginSearch("not-started-certs")}
                  className="group flex items-center gap-3 px-4 py-3 transition-colors hover:bg-accent/40"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-foreground group-hover:text-brand-blue">
                      {it.personName}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {it.certificationName}
                      {it.assignedAt ? ` · assigned ${formatDateOnly(it.assignedAt)}` : ""}
                    </div>
                  </div>
                  <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-3xs font-semibold text-muted-foreground">
                    Not started
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
